import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { CommandCenterPersonProfile } from '@ndvr/customer-api';

import { getPersonOrgName, getPersonPrimaryEmail } from 'utils';
import { useDeletePersonOrg, usePersonProfilesForTenant } from 'views/person/person-queries';
import { ButtonWithConfirmModal, Card, CondensedTable, DateTimeDisplay, ModalText, TextLink } from 'widgets';

const columnHelper = createColumnHelper<CommandCenterPersonProfile>();

export const DuplicateUsers = () => {
    const { data: personProfiles, isLoading } = usePersonProfilesForTenant();

    const groupedPersonProfiles = useMemo(() => {
        const personProfilesMap = new Map<string, CommandCenterPersonProfile[]>();
        personProfiles?.forEach((personProfile) => {
            const email = getPersonPrimaryEmail(personProfile.personOrgShortProfile) ?? '';
            personProfilesMap.set(email, [...(personProfilesMap.get(email) ?? []), personProfile]);
        });
        return Array.from(personProfilesMap);
    }, [personProfiles]);

    return (
        <div className="flex flex-col gap-2.5">
            {groupedPersonProfiles.map(([email, groupedData]) =>
                1 < groupedData.length ? (
                    <Card $padding={0} key={email}>
                        <CondensedTable columns={columns} data={groupedData} isLoading={isLoading} />
                    </Card>
                ) : null,
            )}
        </div>
    );
};

const columns = [
    columnHelper.accessor((data) => getPersonOrgName(data.personOrgShortProfile), {
        id: 'name',
        header: 'Name',
        cell: ({ getValue, row }) => (
            <TextLink
                onClick={(ev) => ev.stopPropagation()}
                to={`/person/${row.original.personOrgShortProfile.personOrgGUID}`}
            >
                {getValue()}
            </TextLink>
        ),
        sortingFn: 'alphanumeric',
        size: 400,
    }),
    columnHelper.accessor((data) => getPersonPrimaryEmail(data.personOrgShortProfile), {
        id: 'primaryEmail',
        header: 'Primary Email',
        sortingFn: 'alphanumeric',
    }),
    columnHelper.accessor((data) => data?.disabled, {
        id: 'disabled',
        header: 'disabled',
        size: 150,
    }),
    columnHelper.accessor('lastLoginAt', {
        id: 'lastLogin',
        header: 'Last Login',
        cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
        sortingFn: 'datetime',
        size: 150,
    }),
    columnHelper.accessor((data) => data, {
        id: 'personOrgGUID',
        header: '',
        cell: ({ getValue }) => <DeletePersonButton commandCenterPersonProfile={getValue()} />,
        sortingFn: 'datetime',
        size: 200,
    }),
];

const DeletePersonButton = ({
    commandCenterPersonProfile,
}: {
    commandCenterPersonProfile: CommandCenterPersonProfile;
}) => {
    const { mutate: deletePersonOrg } = useDeletePersonOrg(
        commandCenterPersonProfile?.personOrgShortProfile.personOrgGUID,
    );
    const personName = getPersonOrgName(commandCenterPersonProfile?.personOrgShortProfile);

    return (
        <ButtonWithConfirmModal
            buttonProps={{
                size: 'small',
                buttonType: 'secondary',
                children: 'Delete Person',
                disabled: !commandCenterPersonProfile?.disabled || !!commandCenterPersonProfile?.lastLoginAt,
            }}
            modalTitle={`Confirm to delete person "${personName}"`}
            onConfirm={deletePersonOrg}
        >
            <ModalText>
                Are you sure you want to delete{' '}
                {`"${personName}" ${commandCenterPersonProfile?.personOrgShortProfile?.personOrgGUID}`}?
            </ModalText>
        </ButtonWithConfirmModal>
    );
};
